import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ApiSession from "../api/ApiSession";
import Accordion from "./Accordions/Accordion";
const apiSession = new ApiSession();

const DATA_TYPES = [
   "INT16", "UINT16", "INT32"
];

const ACCESS_TYPE_OPTIONS = [{ label: "Read", value: "ro" }, { label: "Write", value: "wo" }, { label: "Read & Write", value: "rw" }];
const DEFAULT_ACCESS_TYPE = "ro";
const TEXT_ONLY_REGEX = /^[a-z][A-Za-z]*$/;

const TagListManagement = (props) => {
  const [resourceData, setResourceData] = useState({ resources: [] });
  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [modalError, setModalError] = useState("");
  const [snackBar, setSnackBar] = useState({ type: "", message: "" });
  const [editableTag, setEditableTag] = useState({
    tagName: "",
    displayName: "",
    param: "",
    registerNumber: "",
    dataType: "",
    scaling: "",
    accessType: DEFAULT_ACCESS_TYPE,
    pushToCloud: false,
    swapWords: false,
  });
  const [popupStatus, setPopupStatus] = useState({
    editTagPopupStatus: false,
    deleteConfirmPopUpStatus: false,
  });
  const SCROLL_ROW_THRESHOLD = 10;
  const TABLE_SCROLL_MAX_HEIGHT = 420;
  const fixedCellStyle = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
  const actionCellStyle = {
    minWidth: "88px",
    whiteSpace: "nowrap",
    overflow: "visible",
  };
  const toSafeId = (value) =>
    String(value ?? "")
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, "-");

  useEffect(() => {
    const projectId = props?.projectId;
    if (projectId) {
      loadResourcesData(projectId);
    }
  }, [props?.projectId]);

  const loadResourcesData = (projectId) => {
    apiSession
      .getResources(projectId)
      .then((response) => {
        console.log("Resources data fetched:", response);
        const payload = response && typeof response === "object" ? response : {};

        const emptyResponse = {
          siteId: null,
          projectId: projectId,
          resources: null,
          message: null,
          status: null,
          timestamp: null,
        };

        const normalizeAndSetResourceData = (data) => {
          const normalizedPayload = data && typeof data === "object" ? data : {};
          const normalizedResources = Array.isArray(normalizedPayload?.resources)
            ? normalizedPayload.resources
            : Array.isArray(normalizedPayload)
              ? normalizedPayload
              : [];

          setResourceData({
            ...normalizedPayload,
            resources: normalizedResources,
          });
        };

        const isEmptyPayload =
          !response ||
          (Array.isArray(response)
            ? response.length === 0
            : typeof response === "object" &&
              (Object.keys(response).length === 0 || response?.resources == null));

        if (isEmptyPayload) {
          apiSession
            .saveResources(emptyResponse)
            .then((savedResponse) => {
              normalizeAndSetResourceData(savedResponse);
            })
            .catch((saveError) => {
              console.error("Error saving default resources data:", saveError);
              normalizeAndSetResourceData(emptyResponse);
            });
          return;
        }

        normalizeAndSetResourceData(payload);
      })
      .catch((error) => {
        console.error("Error fetching resources data:", error);
        setResourceData({ resources: [] });
        callSnackbar("error", "Failed to load tag resources. Please try again.");
      });
  };

  const callSnackbar = (type, message) => {
    setSnackBar({ type, message });
    const snackBarElement = document.getElementById("custom-taglistpage");
    if (snackBarElement) {
      snackBarElement.open = true;
    }
  };

  const isSameResource = (resource, targetResource) => {
    if (!resource || !targetResource) {
      return false;
    }

    if (resource?.resourceId && targetResource?.resourceId) {
      return resource.resourceId === targetResource.resourceId;
    }

    return (
      String(resource?.resourceType || "").toLowerCase() ===
        String(targetResource?.resourceType || "").toLowerCase() &&
      String(resource?.resourceName || "").toLowerCase() ===
        String(targetResource?.resourceName || "").toLowerCase()
    );
  };

  const isSameTag = (tag, targetTag) => {
    if (!tag || !targetTag) {
      return false;
    }

    if (tag?.id != null && targetTag?.id != null) {
      return tag.id === targetTag.id;
    }

    return (
      String(tag?.tagId || "") === String(targetTag?.tagId || "") &&
      String(tag?.tagName || "") === String(targetTag?.tagName || "") &&
      String(tag?.param || "") === String(targetTag?.param || "")
    );
  };

  const getDuplicateRegisterNumberDetails = (registerNumber, currentTag = null, currentResource = null) => {
    const normalizedRegisterNumber = String(registerNumber || "").trim();
    if (!normalizedRegisterNumber) {
      return null;
    }

    for (const resource of resourceData?.resources || []) {
      for (const tag of resource?.tagList || []) {
        const isCurrentEditingTag =
          currentTag &&
          currentResource &&
          isSameResource(resource, currentResource) &&
          isSameTag(tag, currentTag);

        if (isCurrentEditingTag) {
          continue;
        }

        if (String(tag?.registerNumber || "").trim() === normalizedRegisterNumber) {
          return {
            registerNumber: normalizedRegisterNumber,
            resourceName: resource?.resourceName || "Unnamed Resource",
            tagName: tag?.tagName || "Unnamed Tag",
          };
        }
      }
    }

    return null;
  };

  const buildRegisterNumberDuplicateMessage = (duplicateDetails) => {
    if (!duplicateDetails) {
      return "Register Number already exists in tag list.";
    }

    return `Register Number ${duplicateDetails.registerNumber} already exists in Resource: ${duplicateDetails.resourceName}, Tag: ${duplicateDetails.tagName}.`;
  };

  const persistResources = (resources) => {
    const payload = {
      siteId: resourceData?.siteId ?? null,
      projectId: resourceData?.projectId ?? props?.projectId ?? null,
      resources,
    };

    apiSession.saveResources(payload)
    .then(res => {
      callSnackbar("success", "Tag added successfully.");
    })
    .catch((error) => {
      console.error("Error saving resources data:", error);
      callSnackbar("error", "Failed to save tag changes. Please try again.");
    });
  };

  const openTagEditPopup = (resource, tag) => {
    if (isProjectReadOnly) {
      return;
    }

    setIsAddMode(false);
    setModalError("");
    setSelectedResource(resource);
    setSelectedTag(tag);
    const currentAccessType =
      tag?.operations ?? tag?.accessType ?? DEFAULT_ACCESS_TYPE;

    setEditableTag({
      tagName: tag?.tagName ?? "",
      displayName: tag?.displayName ?? "",
      param: tag?.param ?? "",
      registerNumber: tag?.registerNumber ?? "",
      dataType: tag?.dataType ?? "",
      scaling: tag?.scaling ?? "",
      accessType: ACCESS_TYPE_OPTIONS.map(option => option.value).includes(currentAccessType)
        ? currentAccessType
        : DEFAULT_ACCESS_TYPE,
      pushToCloud: Boolean(tag?.pushToCloud),
      swapWords: Boolean(tag?.swapWords),
    });
    setPopupStatus((prevState) => ({
      ...prevState,
      editTagPopupStatus: true,
    }));
  };

  const closeTagEditPopup = () => {
    setPopupStatus((prevState) => ({
      ...prevState,
      editTagPopupStatus: false,
    }));
    setIsAddMode(false);
    setModalError("");
    setSelectedResource(null);
    setSelectedTag(null);
  };

  const openAddTagPopup = (resource) => {
    if (isProjectReadOnly) {
      return;
    }

    setIsAddMode(true);
    setModalError("");
    setSelectedResource(resource);
    setSelectedTag(null);
    setEditableTag({
      tagName: "",
      displayName: "",
      param: "",
      registerNumber: "",
      dataType: "",
      scaling: "",
      accessType: DEFAULT_ACCESS_TYPE,
      pushToCloud: false,
      swapWords: false,
    });
    setPopupStatus((prevState) => ({
      ...prevState,
      editTagPopupStatus: true,
    }));
  };

  const openDeleteConfirmPopup = (resource, tag) => {
    if (isProjectReadOnly) {
      return;
    }

    closeTagEditPopup();
    setSelectedResource(resource);
    setSelectedTag(tag);
    setPopupStatus((prevState) => ({
      ...prevState,
      deleteConfirmPopUpStatus: true,
    }));
  };

  const closeDeletePopup = () => {
    setPopupStatus((prevState) => ({
      ...prevState,
      deleteConfirmPopUpStatus: false,
    }));
    setSelectedResource(null);
    setSelectedTag(null);
  };

  const handleTagFieldChange = (event) => {
    const { name, value, checked, type } = event.target;
    const isTextOnlyField = ["tagName", "param", "displayName"].includes(name);
    if (modalError) {
      setModalError("");
    }

    if (isTextOnlyField && value !== "" && !TEXT_ONLY_REGEX.test(value)) {
      return;
    }

    if (name === "registerNumber") {
      const sanitizedRegisterNumber = String(value || "")
        .replace(/\D/g, "")
        .slice(0, 8);

      setEditableTag((prevState) => ({
        ...prevState,
        registerNumber: sanitizedRegisterNumber,
      }));
      return;
    }

    if (name === "scaling") {
      const onlyNumberAndDot = String(value || "").replace(/[^0-9.]/g, "");
      const decimalParts = onlyNumberAndDot.split(".");
      const sanitizedScaling =
        decimalParts.length > 1
          ? `${decimalParts[0]}.${decimalParts.slice(1).join("")}`
          : decimalParts[0];

      setEditableTag((prevState) => ({
        ...prevState,
        scaling: sanitizedScaling,
      }));
      return;
    }

    setEditableTag((prevState) => ({
      ...prevState,
      [name]: name === "swapWords" ? value === "true" : type === "checkbox" ? checked : value,
    }));
  };

  const handleSaveTag = () => {
    if (isProjectReadOnly) {
      closeTagEditPopup();
      return;
    }

    if (!selectedResource) {
      closeTagEditPopup();
      return;
    }

    if (isAddMode) {
      const normalizedTagName = String(editableTag?.tagName || "").trim();
      const normalizedRegisterNumber = String(editableTag?.registerNumber || "").trim();
      const normalizedProperty = String(editableTag?.param || "").trim();
      const normalizedAlias = String(editableTag?.displayName || "").trim();
      if (!normalizedTagName) {
        callSnackbar("error","Tag Name is mandatory.");
        return;
      }

      if (!TEXT_ONLY_REGEX.test(normalizedTagName)) {
        callSnackbar("error", "Tag Name accepts only letters and spaces.");
        return;
      }

      if (normalizedProperty && !TEXT_ONLY_REGEX.test(normalizedProperty)) {
        callSnackbar("error", "Property accepts only letters and spaces.");
        return;
      }

      if (normalizedAlias && !TEXT_ONLY_REGEX.test(normalizedAlias)) {
        callSnackbar("error", "Alias accepts only letters and spaces.");
        return;
      }

      const duplicateTag = (selectedResource?.tagList || []).some(
        (tag) => String(tag?.tagName || "").toLowerCase() === normalizedTagName.toLowerCase()
      );

      if (duplicateTag) {
        callSnackbar("error","Tag Name already exists for this resource.");
        return;
      }

      if (normalizedRegisterNumber) {
        const duplicateRegisterNumber = getDuplicateRegisterNumberDetails(normalizedRegisterNumber);

        if (duplicateRegisterNumber) {
          callSnackbar("error", buildRegisterNumberDuplicateMessage(duplicateRegisterNumber));
          return;
        }
      }

      const newTag = {
        id: null,
        tagId: null,
        configurationType: "CUSTOM",
        derType: selectedResource?.tagList?.[0]?.derType || null,
        tagName: normalizedTagName,
        displayName: editableTag.displayName || normalizedTagName,
        param: editableTag.param,
        registerNumber: editableTag.registerNumber === "" ? null : editableTag.registerNumber,
        dataType: editableTag.dataType,
        scaling: editableTag.scaling === "" ? null : editableTag.scaling,
        operations: ACCESS_TYPE_OPTIONS.map(option => option.value).includes(editableTag.accessType)
          ? editableTag.accessType
          : DEFAULT_ACCESS_TYPE,
        pushToCloud: Boolean(editableTag.pushToCloud),
        swapWords: Boolean(editableTag.swapWords),
        scopeLevel: "DER",
        proposedTagName: "",
        dataProcessingType: null,
        description: null,
        mandatory: false,
        category: "resource",
        monitored: null,
        samplingIntervalSec: null,
        protocol: "Modbus TCP",
        comPort: 502,
        unit: "-",
        multiplicationFactor: null,
        cloudName: null,
        coreName: null,
        dataSource: null,
        recreateTag: null,
        status: null,
        message: null

      };

      const updatedResources = (resourceData?.resources || []).map((resource) => {
        if (!isSameResource(resource, selectedResource)) {
          return resource;
        }

        return {
          ...resource,
          tagList: [...(resource?.tagList || []), newTag],
        };
      });

      setResourceData((prevState) => ({
        ...prevState,
        resources: updatedResources,
      }));
      persistResources(updatedResources);
     
      closeTagEditPopup();
      return;
    }

    if (!selectedTag) {
      closeTagEditPopup();
      return;
    }

    const normalizedTagName = String(editableTag?.tagName || "").trim();
    const normalizedRegisterNumber = String(editableTag?.registerNumber || "").trim();
    const normalizedProperty = String(editableTag?.param || "").trim();
    const normalizedAlias = String(editableTag?.displayName || "").trim();

    if (!normalizedTagName) {
      callSnackbar("error", "Tag Name is mandatory.");
      return;
    }

    if (!TEXT_ONLY_REGEX.test(normalizedTagName)) {
      callSnackbar("error", "Tag Name accepts only letters and spaces.");
      return;
    }

    if (normalizedProperty && !TEXT_ONLY_REGEX.test(normalizedProperty)) {
      callSnackbar("error", "Property accepts only letters and spaces.");
      return;
    }

    if (normalizedAlias && !TEXT_ONLY_REGEX.test(normalizedAlias)) {
      callSnackbar("error", "Alias accepts only letters and spaces.");
      return;
    }

    const duplicateTagName = (selectedResource?.tagList || []).some(
      (tag) => !isSameTag(tag, selectedTag) && String(tag?.tagName || "").toLowerCase() === normalizedTagName.toLowerCase()
    );

    if (duplicateTagName) {
      callSnackbar("error", "Tag Name already exists for this resource.");
      return;
    }

    if (normalizedRegisterNumber) {
      const duplicateRegisterNumber = getDuplicateRegisterNumberDetails(
        normalizedRegisterNumber,
        selectedTag,
        selectedResource
      );

      if (duplicateRegisterNumber) {
        callSnackbar("error", buildRegisterNumberDuplicateMessage(duplicateRegisterNumber));
        return;
      }
    }

    const updatedResources = (resourceData?.resources || []).map((resource) => {
      if (!isSameResource(resource, selectedResource)) {
        return resource;
      }

      return {
        ...resource,
        tagList: (resource?.tagList || []).map((tag) => {
          if (!isSameTag(tag, selectedTag)) {
            return tag;
          }

          return {
            ...tag,
            tagName: normalizedTagName,
            displayName: editableTag.displayName,
            param: editableTag.param,
            registerNumber:
              editableTag.registerNumber === "" ? null : editableTag.registerNumber,
            dataType: editableTag.dataType,
            scaling: editableTag.scaling === "" ? null : editableTag.scaling,
            operations: ACCESS_TYPE_OPTIONS.map(option => option.value).includes(editableTag.accessType)
              ? editableTag.accessType
              : DEFAULT_ACCESS_TYPE,
            pushToCloud: Boolean(editableTag.pushToCloud),
            swapWords: Boolean(editableTag.swapWords),
          };
        }),
      };
    });

    setResourceData((prevState) => ({
      ...prevState,
      resources: updatedResources,
    }));
    persistResources(updatedResources);
    callSnackbar("success", "Tag updated successfully.");
    closeTagEditPopup();
  };

  const handleDeleteTag = () => {
    if (isProjectReadOnly) {
      closeDeletePopup();
      return;
    }

    if (!selectedResource || !selectedTag) {
      closeDeletePopup();
      return;
    }

    const updatedResources = (resourceData?.resources || []).map((resource) => {
      if (!isSameResource(resource, selectedResource)) {
        return resource;
      }

      return {
        ...resource,
        tagList: (resource?.tagList || []).filter((tag) => !isSameTag(tag, selectedTag)),
      };
    });

    setResourceData((prevState) => ({
      ...prevState,
      resources: updatedResources,
    }));
    persistResources(updatedResources);
    callSnackbar("success", "Tag deleted successfully.");
    closeDeletePopup();
  };

  const resourcesWithTags = (resourceData?.resources || []).filter(
    (resource) => Array.isArray(resource?.tagList) && resource.tagList.length > 0
  );

  const groupedByResourceType = resourcesWithTags.reduce((acc, resource) => {
    const type = String(resource?.resourceType || "Unknown").trim() || "Unknown";

    if (!acc[type]) {
      acc[type] = [];
    }

    acc[type].push(resource);
    return acc;
  }, {});

  const typeEntries = Object.entries(groupedByResourceType);
  const isProjectReadOnly = ["COMPLETED", "COMPLETED_AND_SEND"].includes(
    props?.projectStatusData?.projectStatus
  );

  const isEditingResource = (resource) =>
    popupStatus.editTagPopupStatus && isSameResource(resource, selectedResource);

  const renderEditableField = (name, label) => (
    name === "dataType" ? (
      <select
        className="form-control form-control-sm"
        aria-label={label}
        name={name}
        value={editableTag.dataType}
        onChange={handleTagFieldChange}
      >
        <option value="">Select data type</option>
        {editableTag.dataType && !DATA_TYPES.includes(editableTag.dataType) && (
          <option value={editableTag.dataType} disabled>{editableTag.dataType}</option>
        )}
        {DATA_TYPES.map((dataType) => (
          <option key={dataType} value={dataType}>
            {dataType === "FLOAT64" ? "FLOAT64 (DOUBLE)" : dataType}
          </option>
        ))}
      </select>
    ) : name === "accessType" ? (
      <select
        className="form-control form-control-sm"
        aria-label={label}
        name={name}
        value={ACCESS_TYPE_OPTIONS.map(option => option.value).includes(editableTag.accessType) ? editableTag.accessType : DEFAULT_ACCESS_TYPE}
        onChange={handleTagFieldChange}
      >
        {ACCESS_TYPE_OPTIONS.map((accessType) => (
          <option key={accessType.label} value={accessType.value}>
            {accessType.label}
          </option>
        ))}
      </select>
    ) : (
    <input
      type="text"
      className="form-control form-control-sm"
      aria-label={label}
      name={name}
      value={editableTag[name]}
      onChange={handleTagFieldChange}
      pattern={["tagName", "param", "displayName"].includes(name) ? "[A-Za-z\\s]+" : name === "registerNumber" ? "[0-9]*" : name === "scaling" ? "[0-9]*[.]?[0-9]*" : undefined}
      inputMode={name === "registerNumber" ? "numeric" : name === "scaling" ? "decimal" : undefined}
      maxLength={name === "registerNumber" ? 8 : undefined}
    />
    )
  );
  const capitalize = (str) => {
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
  }
 const getTagTitle = (tag) => {
   return ACCESS_TYPE_OPTIONS.filter(option => option.value === tag?.operations )[0]?.label ?? tag?.accessType ?? "-";
 };
  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="se-font-14px-bold se-life-green mb-0">
          Tags Management Tables
        </div>
      </div>

      {typeEntries.length > 0 && (
        <Accordion allowMultipleOpen>
          {typeEntries.map(([resourceType, resources]) => (
            <div
              key={`resource-type-${toSafeId(resourceType)}`}
              className="col-md-12"
              label={`${capitalize(resourceType)}`}
              status={1}
              hideStatus={true}
              displayStatus="1"
              icon="notification_error_full_stroke"
            >

              <Accordion allowMultipleOpen>
                {resources.map((resource) => (
                  <div
                    key={resource?.resourceId || `${resource?.resourceType}-${resource?.resourceName}`}
                    className="col-md-12"
                    label={`${capitalize(resource?.resourceName )|| "Unnamed Resource"}`}
                    status={1}
                    hideStatus={true}
                    displayStatus="1"
                    icon="notification_error_full_stroke"
                  >
                    <div className={`d-flex justify-content-between align-items-center mb-2 ${isProjectReadOnly ? "se-block-disabled" : ""}`}>
                      <div className="se-font-12px">{`Total Tags: ${resource?.tagList?.length || 0}`}</div>
                    </div>
                    <div
                      className={`table-responsive ${isProjectReadOnly ? "se-block-disabled" : ""}`}
                      style={{
                        maxHeight:
                          (resource?.tagList?.length || 0) > SCROLL_ROW_THRESHOLD
                            ? `${TABLE_SCROLL_MAX_HEIGHT}px`
                            : "none",
                        overflowY:
                          (resource?.tagList?.length || 0) > SCROLL_ROW_THRESHOLD
                            ? "auto"
                            : "hidden",
                        overflowX: "auto",
                        border: "1px solid #dee2e6",
                        pointerEvents: isProjectReadOnly ? "auto" : undefined,
                      }}
                    >
                      <table
                        className="table table-bordered table-hover mb-0"
                        aria-label={`${resource?.resourceName || "Resource"} Tags`}
                        style={{ tableLayout: "fixed", width: "100%", minWidth: isEditingResource(resource) ? "900px" : undefined }}
                      >
                        <colgroup>
                          <col style={{ width: "9%" }} />
                          <col style={{ width: "9%" }} />
                          <col style={{ width: "9%" }} />
                          <col style={{ width: "9%" }} />
                          <col style={{ width: "9%" }} />
                          <col style={{ width: "9%" }} />
                          <col style={{ width: "9%" }} />
                          <col style={{ width: "9%" }} />
                          <col style={{ width: "9%" }} />
                          <col style={{ width: "9%" }} />
                          <col style={{ width: "10%" }} />
                        </colgroup>
                        <thead className="thead-light">
                          <tr>
                            <th style={fixedCellStyle} title="Name">Tag Name</th>
                            <th style={fixedCellStyle} title="Asset Type">Asset Type</th>
                            <th style={fixedCellStyle} title="Register Number">Register Number</th>
                            <th style={fixedCellStyle} title="Data Type">Data Type</th>
                            <th className={isEditingResource(resource) ? "" : "d-none d-md-table-cell"} style={fixedCellStyle} title="Scaling  Factor">
                              Scaling Factor
                            </th>
                            <th className={isEditingResource(resource) ? "" : "d-none d-md-table-cell"} title="Property">
                              Property
                            </th>
                            <th style={fixedCellStyle} title="Alias">Alias</th>
                            <th style={fixedCellStyle} title="Swap Words">Swap Words</th>
                            <th style={fixedCellStyle} title="Enabled">Enabled</th>
                             <th style={fixedCellStyle} title="Access Type">Access Type</th>
                            <th style={actionCellStyle} title="Actions">Actions</th>
                          </tr>
                        </thead>

                        <tbody>
                          {resource?.tagList?.map((tag, index) => {
                            const isEditing = !isAddMode && isEditingResource(resource) && isSameTag(tag, selectedTag);
                            const checkboxId = `tag-enabled-${toSafeId(
                              resource?.resourceId || resource?.resourceName
                            )}-${toSafeId(tag?.id ?? index)}`;

                            return (
                              <tr key={`${resource?.resourceId || resource?.resourceName}-${tag?.id ?? index}`}>
                                <td className="text-nowrap" style={fixedCellStyle} title={tag?.tagName ?? "-"}>
                                  {isEditing ? renderEditableField("tagName", "Tag Name") : tag?.tagName ?? "-"}
                                </td>

                                <td className="text-nowrap" style={fixedCellStyle} title={tag?.derType ?? resourceType ?? "-"}>
                                  {tag?.derType ?? resourceType ?? "-"}
                                </td>

                                <td style={fixedCellStyle} title={tag?.registerNumber ?? "-"}>
                                  {isEditing ? renderEditableField("registerNumber", "Register Number") : tag?.registerNumber ?? "-"}
                                </td>

                                <td style={fixedCellStyle} title={tag?.dataType ?? "-"}>
                                  {isEditing ? renderEditableField("dataType", "Data Type") : tag?.dataType ?? "-"}
                                </td>

                                <td className={isEditingResource(resource) ? "text-center" : "d-none d-md-table-cell text-center"} style={fixedCellStyle} title={tag?.scaling ?? "-"}>
                                  {isEditing ? renderEditableField("scaling", "Scaling") : tag?.scaling ?? "-"}
                                </td>

                                <td className={isEditingResource(resource) ? "" : "d-none d-md-table-cell"} style={fixedCellStyle} title={tag?.param ?? "-"}>
                                  {isEditing ? renderEditableField("param", "Property") : tag?.param ?? "-"}
                                </td>

                                <td style={fixedCellStyle} title={tag?.displayName ?? "-"}>
                                  {isEditing ? renderEditableField("displayName", "Alias") : tag?.displayName ?? "-"}
                                </td>

                                <td className="text-center" title={tag?.swapWords ?? "-"}>
                                  {isEditing ? (
                                    <select
                                      className="form-control form-control-sm"
                                      name="swapWords"
                                      aria-label="Swap Words"
                                      value={String(Boolean(editableTag.swapWords))}
                                      onChange={handleTagFieldChange}
                                      disabled={tag?.configurationType === "DEFAULT" || !isEditing}
                                    >
                                      <option value="true">true</option>
                                      <option value="false">false</option>
                                    </select>
                                  ) : String(Boolean(tag?.swapWords))}
                                </td>

                                <td className="text-center" title={tag?.pushToCloud ?? "-"}>
                                  <div className={`custom-control custom-checkbox  ${tag?.configurationType === "DEFAULT" ? "se-block-disabled" : ""}`}>
                                    <input
                                      type="checkbox"
                                      className="custom-control-input"
                                      id={checkboxId}
                                      name="pushToCloud"
                                      aria-label="Enabled"
                                      checked={Boolean(isEditing ? editableTag.pushToCloud : tag?.pushToCloud)}
                                      onChange={isEditing ? handleTagFieldChange : undefined}
                                      readOnly={!isEditing}
                                      disabled={tag?.configurationType === "DEFAULT" ? true : !isEditing}
                                    />

                                    <label
                                      className="custom-control-label"
                                      htmlFor={checkboxId}
                                    />
                                  </div>
                                </td>

                                <td style={fixedCellStyle} title={getTagTitle(tag)}>
                                  {isEditing ? renderEditableField("accessType", "Access Type") : tag?.operations ?? tag?.accessType ?? "-"}
                                </td>

                                <td className="text-center" style={actionCellStyle}>
                                  {isEditing ? (
                                    <div className="d-flex flex-row justify-content-center align-items-center">
                                      <se-icon
                                        size="medium"
                                        color="primary"
                                        title="Click to update"
                                        onClick={handleSaveTag}
                                      >
                                        notification_ok_stroke
                                      </se-icon>
                                      <se-icon
                                        size="medium"
                                        title="Click to cancel"
                                        onClick={closeTagEditPopup}
                                        style={{ color: "red" }}
                                      >
                                        action_delete_cross
                                      </se-icon>
                                    </div>
                                  ) : isProjectReadOnly ? (
                                    <se-icon size="small" disabled>action_lock_on</se-icon>
                                  ) : (
                                    <>
                                      {tag?.configurationType === "DEFAULT" ? <se-icon size="small" disabled>action_lock_on</se-icon> : <span className="pointer">
                                        <se-icon
                                          size="small"
                                          title="Edit"
                                          color="primary"
                                          onClick={() => openTagEditPopup(resource, tag)}
                                        >
                                          action_editor
                                        </se-icon>
                                      </span>}
                                      <span>&nbsp; &nbsp;</span>
                                      {tag?.configurationType !== "DEFAULT" && (<span className="pointer">
                                        <se-icon
                                          size="small"
                                          title="Delete"
                                          color="primary"
                                          onClick={() => openDeleteConfirmPopup(resource, tag)}
                                        >
                                          action_delete
                                        </se-icon>
                                      </span>)}
                                    </>
                                  )}
                                </td>

                              </tr>
                            );
                          })}
                          <tr aria-label="Add Tag">
                            {isAddMode && isEditingResource(resource) ? (
                              <>
                                <td>{renderEditableField("tagName", "Tag Name")}</td>
                                <td style={fixedCellStyle}>{selectedResource?.tagList?.[0]?.derType}</td>
                                <td>{renderEditableField("registerNumber", "Register Number")}</td>
                                <td>{renderEditableField("dataType", "Data Type")}</td>
                                <td>{renderEditableField("scaling", "Scaling")}</td>
                                <td>{renderEditableField("param", "Property")}</td>
                                <td>{renderEditableField("displayName", "Alias")}</td>
                                <td className="text-center">
                                 <select
                                    className="form-control form-control-sm"
                                    aria-label="Swap Words"
                                    name="swapWords"
                                    value={String(editableTag.swapWords)}
                                    onChange={handleTagFieldChange}
                                  >
                                    <option value="true">true</option>
                                    <option value="false">false</option>
                                  </select>
                                </td>
                                <td className="text-center">
                                  <div className="custom-control custom-checkbox">
                                    <input
                                      type="checkbox"
                                      className="custom-control-input"
                                      id="new-tag-enabled"
                                      aria-label="Enabled"
                                      name="pushToCloud"
                                      checked={Boolean(editableTag.pushToCloud)}
                                      onChange={handleTagFieldChange}
                                    />
                                    <label className="custom-control-label" htmlFor="new-tag-enabled" />
                                  </div>
                                </td>
                                <td style={fixedCellStyle}>{renderEditableField("accessType", "Access Type")}</td>
                              </>
                            ) : (
                              <>
                                <td />
                                <td />
                                <td />
                                <td />
                                <td className={isEditingResource(resource) ? "" : "d-none d-md-table-cell"} />
                                <td className={isEditingResource(resource) ? "" : "d-none d-md-table-cell"} />
                                <td />
                                <td />
                                <td />
                                <td />
                              </>
                            )}
                            <td className="text-center" style={actionCellStyle}>
                              {isAddMode && isEditingResource(resource) ? (
                                <div className="d-flex flex-row justify-content-center align-items-center">
                                  <se-icon size="medium" color="primary" title="Click to update" onClick={handleSaveTag}>
                                    notification_ok_stroke
                                  </se-icon>
                                  <se-icon size="medium" title="Click to cancel" onClick={closeTagEditPopup} style={{ color: "red" }}>
                                    action_delete_cross
                                  </se-icon>
                                </div>
                              ) : isProjectReadOnly ? (
                                <se-icon size="medium" disabled>
                                  action_lock_on
                                </se-icon>
                              ) : (
                                <se-icon size="medium" color="primary" title="Click to add" onClick={() => openAddTagPopup(resource)}>
                                  action_add_flat
                                </se-icon>
                              )}
                            </td>
                          </tr>
                          {isAddMode && isEditingResource(resource) && modalError && (
                            <tr>
                              <td colSpan={10}>
                                <div role="alert" className="se-error-message-note">{modalError}</div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </Accordion>
            </div>
          ))}
        </Accordion>
      )}

      {typeEntries.length === 0 && (
        <div className="se-font-12px text-muted">
          No tag resources found for this project.
        </div>
      )}

      <se-dialog
        id="dialog-complex-delete-confirm"
        open={popupStatus.deleteConfirmPopUpStatus}
        can-backdrop="false"
      >
        <se-dialog-header>
          Confirmation
        </se-dialog-header>

        <se-dialog-content>
          <span className="se-font-14px">
            {`Do you confirm to delete the tag ${selectedTag?.tagName || ""} ?`}
          </span>
        </se-dialog-content>

        <se-dialog-footer>
          <se-button onClick={closeDeletePopup}>Cancel</se-button>
          <se-button option="flat" color="primary" onClick={handleDeleteTag}>
            OK
          </se-button>
        </se-dialog-footer>
      </se-dialog>

      <se-snackbar
        id="custom-taglistpage"
        type={snackBar.type}
        message={snackBar.message}
        icon="information_stroke"
        duration="3000"
      ></se-snackbar>
    </>
  );
};

TagListManagement.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

TagListManagement.defaultProps = {};
export default TagListManagement;